import { XCircleOutlineIcon } from '@fountain/ripple/icon';

import { rippleModalHeaderIcon } from 'components/ModalHeaderIcon';
import { PIPELINE_CARD_STAGE_ID } from 'containers/ApplicantsV2/constants';
import { DialogType } from 'containers/MoveApplicantDialog/constants';
import messages from 'containers/MoveApplicantDialog/messages';
import MoveIcon from 'images/move-icon.svg';
import { getApplicantTableScope } from 'utils/urlUtils';

export const isEmptyObject = (obj?: object) =>
  obj && Object.keys(obj).length === 0;

/**
 * The opening and stage a bulk action covers: the ones the applicants table
 * shows. The Pipeline card spans every stage, so it names no stage here.
 */
export const getBulkActionTableScope = (location: { pathname: string }) => {
  const { jobId, stageId } = getApplicantTableScope({ location });
  return {
    jobId,
    stageId: stageId === PIPELINE_CARD_STAGE_ID ? undefined : stageId,
  };
};

/**
 * Get submit button text. Based on `dialogType`.
 *
 * @param {string} dialogType
 *
 * @returns {string} submit button text
 */
export const getActionMessage = (dialogType?: DialogType) => {
  switch (dialogType) {
    case 'moveApplicantToReject':
      return messages.reject;
    case 'moveApplicantToHired':
      return messages.hire;
    case 'moveApplicantToJob':
    case 'moveApplicantToNextStage':
    case 'moveApplicantToOnHold':
    case 'moveApplicantToStage':
    default:
      return messages.submit;
  }
};

const RejectIcon = rippleModalHeaderIcon(XCircleOutlineIcon);

export const getHeaderIcon = (dialogType?: DialogType) => {
  switch (dialogType) {
    case 'moveApplicantToOnHold':
    case 'moveApplicantToReject':
      return RejectIcon;
    case 'moveApplicantToHired':
    case 'moveApplicantToJob':
    case 'moveApplicantToNextStage':
    case 'moveApplicantToStage':
    default:
      return MoveIcon;
  }
};

/**
 * Should we display an icon indicating an adverse action. Based on `dialogType`.
 *
 * @param {string} dialogType
 *
 * @returns {boolean} is icon negative action type
 */
export const getShouldIndicateAdverseAction = (dialogType?: DialogType) => {
  switch (dialogType) {
    case 'moveApplicantToOnHold':
    case 'moveApplicantToReject':
      return true;
    case 'moveApplicantToHired':
    case 'moveApplicantToJob':
    case 'moveApplicantToNextStage':
    case 'moveApplicantToStage':
    default:
      return false;
  }
};

/**
 * Get dialog title. Based on `dialogType`.
 *
 * @param {string} dialogType
 *
 * @returns {string} dialog title
 */
export const getTitleMessage = (dialogType?: DialogType) => {
  switch (dialogType) {
    case 'moveApplicantToJob':
      return messages.moveApplicantToJob;
    case 'moveApplicantToNextStage':
      return messages.moveApplicantToNextStage;
    case 'moveApplicantToOnHold':
      return messages.moveApplicantToOnHold;
    case 'moveApplicantToReject':
      return messages.moveApplicantToReject;
    case 'moveApplicantToHired':
      return messages.moveApplicantToHired;
    case 'moveApplicantToStage':
    default:
      return messages.moveApplicantToStage;
  }
};

/**
 * similar to `Partial` but this will also allow values within nested objects to be optional as well
 */
export type Subset<K> = {
  [attr in keyof K]?: K[attr] extends object ? Subset<K[attr]> : K[attr];
};
